<template>
  <div class="vuestic-page-layout">
    <div class="vuestic-page-layout__content">
      <div class="config-widget">
        <h2 class="config-widget-header">Configurações do Sistema</h2>
        <div class="config-widget-content">
          <p class="text-muted mb-4">Gerencie todas as configurações do sistema em um só lugar.</p>
          
          <!-- Ações globais -->
          <div class="global-actions mb-4">
            <div class="actions-container">
              <div class="actions-info">
                <h5 class="mb-0">Ações globais</h5>
                <p class="text-muted mb-0 mt-1">Aplique todas as alterações pendentes ou restaure para os valores padrão</p>
              </div>
              <div class="actions-buttons">
                <Button @handle:click="applyAllChanges" :disabled="!hasAnyChanges" color="primary" text="Aplicar todas as alterações">
                  <span v-if="pendingChangesCount > 0" class="badge ml-1">{{ pendingChangesCount }}</span>
                </Button>
                <Button @handle:click="resetAll" color="danger" :fill="false" text="Restaurar padrões" class="ml-2">
                </Button>
              </div>
            </div>
          </div>
          <div class="global-actions mb-4">
            <div class="actions-container">
              <div class="actions-info">
                <p class="text-muted mb-0 mt-1">Aplicar configurações na autoescola:</p>
              </div>
              <div class="actions-buttons">
                <Select 
                  class="input-full" 
                  :options="schemaOptions" 
                  legend=""
                  placeholder="Selecione uma autoescola" 
                  :modelValue="schema" 
                  @update:modelValue="$event => schema = $event"
                  :disabled="loading" 
                />
              </div>
            </div>
          </div>

          <div v-if="schema === 'all'" class="alert alert-info mb-4">
            <div class="d-flex align-items-center">
              <i class="fa fa-info-circle me-2"></i>
              <div>
                <strong>Atenção:</strong> Ao selecionar "Todas as autoescolas", as configurações serão definidas com valores padrão do sistema e aplicadas globalmente para todas as autoescolas cadastradas.
              </div>
            </div>
          </div>
    
          <div v-if="schema">
            <div v-if="loading" class="loading-container">
              <LoadingSpinner :size="60" />
              <p class="loading-text">Carregando configurações...</p>
            </div>
            
            <!-- Acordeão de configurações -->
            <div v-else class="accordion-container">

              <Accordion
                title="Alterar comportamento do sistema" 
                icon="cog"
              >
                <!-- Seção: Reconhecimento Facial -->
                <Accordion 
                  title="Reconhecimento Facial" 
                  icon="user"
                  groupId="recognize"
                >
                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: sections.recognize.changed }"></span>
                  </template>
                  
                  <p class="section-description">Configure as preferências do módulo de reconhecimento facial do sistema.</p>
                  
                  <div class="form-group custom-switch-container">
                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" id="facialRecognition" v-model="sections.recognize.active">
                      <label class="custom-control-label" for="facialRecognition">{{ sections.recognize.active ? 'Ativado' : 'Desativado' }}</label>
                    </div>
                  </div>
                  
                  <Input 
                    legend="Alterar percentual de similaridade"
                    :modelValue="sections.recognize.percentage"
                    @update:modelValue="$event => {
                      const value = $event.replace(/\D/g, '');
                      sections.recognize.percentage = value ? Math.min(parseInt(value), 100).toString() : '';
                    }"
                    placeholder="Percentual de similaridade"
                    type="number"
                    :mask="'###'"
                  />
                  
                  <!-- <div class="config-footer">
                    <span class="config-status text-success" :class="{ visible: sections.recognize.saveStatus, 'fade-out': sections.recognize.saveStatus }">
                      <i class="fa fa-check-circle"></i> Configurações salvas com sucesso!
                    </span>
                    <Button 
                      @handle:click="saveSection('recognize')" 
                      text="Salvar Configurações de Reconhecimento Facial" 
                      color="primary"
                      :disabled="!sections.recognize.changed">
                    </Button>
                  </div> -->
                </Accordion>

                <Accordion
                  title="OCR" 
                  icon="eye"
                  groupId="ocr">

                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: sections.ocr.changed }"></span>
                  </template>
                  
                  <p class="section-description">Ativar ou desativar o OCR das placas.</p>
                  
                  <div class="form-group custom-switch-container">
                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" id="placaOCR" v-model="sections.ocr.active">
                      <label class="custom-control-label" for="placaOCR">{{ sections.ocr.active ? 'Ativado' : 'Desativado' }}</label>
                    </div>
                  </div>

                  <!-- <div class="config-footer">
                    <span class="config-status text-success" :class="{ visible: sections.ocr.saveStatus, 'fade-out': sections.ocr.saveStatus }">
                      <i class="fa fa-check-circle"></i> Configurações salvas com sucesso!
                    </span>
                    <Button 
                      @handle:click="saveSection('ocr')" 
                      text="Salvar Configurações de OCR" 
                      color="primary"
                      :disabled="!sections.ocr.changed">
                    </Button>
                  </div> -->
                </Accordion>

                <Accordion
                  title="Veiculos" 
                  icon="car"
                  groupId="Ignicao">

                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: sections.vehicle.changed }"></span>
                  </template>
                  
                  <div class="vehicle-settings-container">
                    <div class="vehicle-setting-row">
                      <div class="vehicle-setting-item">
                        <p class="section-description">Ativar ou desativar verificação da ignição dos veículos.</p>
                        
                        <div class="form-group custom-switch-container">
                          <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="ignicao" v-model="sections.vehicle.active_ignition">
                            <label class="custom-control-label" for="ignicao">{{ sections.vehicle.active_ignition ? 'Ativado' : 'Desativado' }}</label>
                          </div>
                        </div>
                      </div>
                      
                      <div class="vehicle-setting-item">
                        <p class="section-description">Ativar ou desativar validação de perímetro.</p>
                        
                        <div class="form-group custom-switch-container">
                          <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="gps" v-model="sections.vehicle.active_gps">
                            <label class="custom-control-label" for="gps">{{ sections.vehicle.active_gps ? 'Ativado' : 'Desativado' }}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="vehicle-setting-item">
                      <p class="gps-interval-text">
                        Intervalo em atualizações com qual frequência o aplicativo deve informar as coordenadas do GPS.
                      </p>
                      
                      <Input
                        legend="Intervalo de atualização GPS (em segundos)"
                        :modelValue="sections.vehicle.gps_interval"
                        @update:modelValue="$event => {
                          const value = $event.replace(/\D/g, '');
                          sections.vehicle.gps_interval = value ? Math.min(parseInt(value), 3600).toString() : '';
                        }"
                        placeholder="Intervalo em segundos"
                        type="number"
                        :mask="'####'"
                      />
                    </div>
                    
                    <div class="vehicle-setting-item tolerance-section">
                      <h6 class="tolerance-title">Tolerâncias de horário da aula</h6>
                      
                      <div class="tolerance-inputs">
                        <div class="tolerance-input-group">
                          <Input
                            legend="Alterar tolerância inicial (em minutos)"
                            :modelValue="sections.vehicle.tolerance_initial"
                            @update:modelValue="$event => {
                              const value = $event.replace(/\D/g, '');
                              sections.vehicle.tolerance_initial = value ? Math.min(parseInt(value), 100).toString() : '';
                            }"
                            placeholder="Tolerância inicial"
                            type="number"
                            :mask="'###'"
                          />
                        </div>
                        
                        <div class="tolerance-input-group">
                          <Input
                            legend="Alterar tolerância final (em minutos)"
                            :modelValue="sections.vehicle.tolerance_final"
                            @update:modelValue="$event => {
                              const value = $event.replace(/\D/g, '');
                              sections.vehicle.tolerance_final = value ? Math.min(parseInt(value), 100).toString() : '';
                            }"
                            placeholder="Tolerância final"
                            type="number"
                            :mask="'###'"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- <div class="config-footer">
                    <span class="config-status text-success" :class="{ visible: sections.vehicle.saveStatus, 'fade-out': sections.vehicle.saveStatus }">
                      <i class="fa fa-check-circle"></i> Configurações salvas com sucesso!
                    </span>
                    <Button 
                      @handle:click="saveSection('vehicle')" 
                      text="Salvar Configurações de Veículos" 
                      color="primary"
                      :disabled="!sections.vehicle.changed">
                    </Button>
                  </div> -->
                </Accordion>

                <Accordion 
                  title="Teorica" 
                  icon="user"
                  groupId="recognize"
                >
                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: sections.teorica && sections.teorica.changed }"></span>
                  </template>
                  <p class="section-description">Adicione agrupamentos teóricos que poderão ser enviados ao GETRAN sem as fotos das aulas.</p>
                  <div class="form-group" style="align-items: self-end;">
                    <Input
                      legend="Número do agrupamento"
                      :modelValue="novoAgrupamento"
                      @update:modelValue="$event => novoAgrupamento = $event.replace(/\D/g, '')"
                      placeholder="Digite o número do agrupamento"
                      type="number"
                      :mask="'##########'"
                    />
                    <Button
                      @handle:click="adicionarAgrupamento"
                      :disabled="!novoAgrupamento || agrupamentos.includes(novoAgrupamento)"
                      color="primary"
                      text="Adicionar"
                      class="ml-2"
                    />
                  </div>
                  <div class="agrupamentos-lista">
                    <h6>Lista de agrupamentos informados:</h6>
                    <div v-if="agrupamentos.length > 0" class="agrupamentos-inline">
                      <span v-for="(agrupamento, idx) in agrupamentos" :key="agrupamento" class="agrupamento-tag">
                        {{ agrupamento }}
                        <button
                          @click="removerAgrupamento(idx)"
                          class="remove-btn"
                          type="button"
                          title="Remover agrupamento"
                        >
                          ×
                        </button>
                      </span>
                    </div>
                    <p v-else class="text-muted">Nenhum agrupamento informado.</p>
                  </div>
                </Accordion>
                
              </Accordion>
            </div>
          </div>

              <Accordion
                title="Configurações adicionais" 
                icon="cog"
              >

                <Accordion
                  title="Hodômetro" 
                  icon="tachometer-alt"
                  groupId="odometer"
                >
                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: odometer.changed }"></span>
                  </template>
                  
                  <p class="section-description">Altere o valor do hodômetro dos veículos cadastrados no sistema.</p>
                  
                  <div class="odometer-settings-container">
                    <div class="odometer-setting-row">
                      <div class="odometer-setting-item">
                        <Select 
                          class="input-full" 
                          :options="schemaOptionAdditional" 
                          legend="Autoescolas"
                          placeholder="Selecione uma autoescola" 
                          :modelValue="schemaVehicle" 
                          @update:modelValue="$event => schemaVehicle = $event"
                          :disabled="loading" 
                        />
                      </div>
                    
                      <div class="odometer-setting-item">
                        <Select 
                          class="input-full" 
                          :options="vehicleOptions" 
                          legend="Veículo"
                          placeholder="Selecione o veículo" 
                          :modelValue="odometer.vehicle" 
                          @update:modelValue="$event => odometer.vehicle = $event"
                          :disabled="loading || !schemaVehicle" 
                        />
                      </div>
                    </div>
                    
                    <div class="odometer-setting-item mt-3">
                      <Input
                        legend="Novo valor do hodômetro (km)"
                        :modelValue="odometer.value"
                        @update:modelValue="$event => {
                          const value = $event.replace(/\D/g, '');
                          odometer.value = value ? Math.min(parseInt(value), 999999).toString() : '';
                        }"
                        placeholder="Hodômetro atual"
                        type="number"
                        :mask="'######'"
                      />
                      <p class="text-muted mt-2 small-note">
                        <i class="fa fa-info-circle"></i>
                        O valor inserido substituirá o hodômetro atual do veículo.
                      </p>
                    </div>
                  </div>

                  <div class="config-footer">
                    <span class="config-status text-success" :class="{ visible: odometer.saveStatus, 'fade-out': odometer.saveStatus }">
                      <i class="fa fa-check-circle"></i> Hodômetro atualizado com sucesso!
                    </span>
                    <Button 
                      @handle:click="saveHodometro()" 
                      text="Atualizar Hodômetro" 
                      color="primary"
                      :disabled="!odometer.value || !odometer.vehicle">
                    </Button>
                  </div>
                </Accordion>

                <Accordion
                  title="Alertas Telegram" 
                  icon="paper-plane"
                  groupId="telegram"
                  data-testid="accordion-alertas-telegram"
                >
                  <template v-slot:indicator>
                    <span class="changed-indicator" :class="{ visible: telegram.changed }"></span>
                  </template>
                  
                  <p class="section-description">Configure responsáveis para receber alertas de falhas via Telegram.</p>
                  
                  <!-- Mensagem de Orientação DESTACADA -->
                  <div class="alert alert-warning mb-4" style="border-left: 4px solid #ff9800; background-color: #fff3cd;" data-testid="mensagem-orientacao-telegram">
                    <div class="d-flex align-items-start">
                      <i class="fa fa-exclamation-circle me-3" style="font-size: 24px; color: #ff9800; margin-top: 2px;"></i>
                      <div>
                        <strong style="font-size: 16px; color: #856404;">⚠️ IMPORTANTE - Leia antes de cadastrar:</strong>
                        <p style="margin: 8px 0 0 0; color: #856404; font-size: 14px; line-height: 1.6;">
                          1. <strong>Obtenha o @username do Telegram do responsável</strong> - este deve corresponder ao número de telefone cadastrado no Telegram.<br>
                          2. Cadastre o responsável com o @username correto.<br>
                          3. O responsável receberá um e-mail com o link do grupo.<br>
                          4. Após entrar no grupo, o sistema vinculará automaticamente o perfil e ele começará a receber alertas.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Informações do Sistema de Alertas -->
                  <div class="sistema-alertas-info mb-4">
                    <div class="alert alert-success">
                      <div class="d-flex align-items-center">
                        <i class="fa fa-check-circle me-2"></i>
                        <div>
                          <strong>Sistema de Alertas Ativo:</strong> {{ infoGrupoTelegram.nome || 'Notificações - LOGS SISTEMA NOVO CFC' }}<br>
                          <small class="text-muted">Todos os alertas são enviados automaticamente para o grupo do Telegram. Responsáveis cadastrados podem acessar o grupo através do link de convite enviado por email: <a href="https://t.me/+1PUXjxtaDugzZGMx" style="color: blue;" target="_blank">https://t.me/+1PUXjxtaDugzZGMx</a></small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="telegram-settings-container">
                    <!-- Teste de Conexão -->
                    <div class="telegram-test-section mb-4">
                      <div class="test-row">
                        <div class="test-info">
                          <h6>Status da Conexão</h6>
                          <p class="text-muted mb-0">Teste a conexão com o bot do Telegram</p>
                        </div>
                        <Button 
                          @handle:click="testarConexao" 
                          text="Testar Conexão" 
                          color="primary"
                          :fill="true"
                          :disabled="testando">
                          <i class="fa fa-wifi" v-if="!testando"></i>
                          <i class="fa fa-spinner fa-spin" v-else></i>
                        </Button>
                      </div>
                      <div v-if="statusConexao" class="connection-status mt-2" :class="statusConexao.sucesso ? 'success' : 'error'">
                        <i :class="statusConexao.sucesso ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'"></i>
                        {{ statusConexao.mensagem }}
                      </div>
                    </div>

                    <!-- Seção de Responsáveis Cadastrados -->
                    <div class="telegram-section responsaveis-section" data-testid="responsaveis-section">
                      <div class="section-header">
                        <h6>Responsáveis Cadastrados</h6>
                        <Button 
                          @handle:click="abrirModalResponsavel" 
                          text="Adicionar Responsável" 
                          color="primary" 
                          size="small"
                          data-testid="btn-adicionar-responsavel">
                          <i class="fa fa-plus"></i>
                        </Button>
                      </div>
                      
                      <!-- Campo de Busca -->
                      <div v-if="responsaveis.length > 0" class="search-container">
                        <Input
                          legend=""
                          :modelValue="buscaResponsaveis"
                          @update:modelValue="$event => { buscaResponsaveis = $event; filtrarResponsaveis(); }"
                          placeholder="Buscar por nome ou telefone..."
                          type="text"
                        >
                          <template v-slot:prepend>
                            <i class="fa fa-search"></i>
                          </template>
                        </Input>
                      </div>
                      
                      <!-- Loading state -->
                      <div v-if="carregandoResponsaveis" class="loading-state">
                        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
                        <p class="text-muted mt-2">Carregando responsáveis...</p>
                      </div>
                      
                      <!-- Empty state -->
                      <div v-else-if="responsaveis.length === 0" class="empty-state">
                        <i class="fa fa-users fa-2x text-muted"></i>
                        <p class="text-muted mt-2">Nenhum responsável cadastrado</p>
                        <small class="text-muted">Clique em "Adicionar Responsável" para começar</small>
                      </div>
                      
                      <div v-else-if="responsaveisFiltrados.length === 0" class="empty-state">
                        <i class="fa fa-search fa-2x text-muted"></i>
                        <p class="text-muted mt-2">Nenhum responsável encontrado</p>
                      </div>
                      
                      <div v-else class="responsaveis-container">
                        <!-- Lista de Responsáveis Paginados com Scroll -->
                        <div class="responsaveis-scroll-container" data-testid="responsaveis-scroll-container">
                          <div class="responsaveis-list" data-testid="responsaveis-list">
                            <div v-for="responsavel in responsaveisPaginados" :key="responsavel.id" 
                                 class="responsavel-item" :data-testid="`responsavel-item-${responsavel.id}`">
                              <div class="responsavel-info">
                                <div class="nome" data-testid="responsavel-nome">{{ responsavel.nome }}</div>
                                <div class="telefone" data-testid="responsavel-telefone">{{ formatarTelefone(responsavel.numero_telefone) }}</div>
                                <div class="status" :class="responsavel.ativo ? 'ativo' : 'inativo'" 
                                     :data-testid="`responsavel-status-${responsavel.ativo ? 'ativo' : 'inativo'}`">
                                  <i :class="responsavel.ativo ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                                  {{ responsavel.ativo ? 'Ativo' : 'Inativo' }}
                                </div>
                              </div>
                              <div class="responsavel-actions">
                                <Button 
                                  @handle:click="editarResponsavel(responsavel)" 
                                  color="secondary" 
                                  size="small" 
                                  :fill="true"
                                  text=""
                                  :data-testid="`btn-editar-responsavel-${responsavel.id}`">
                                  <i class="fa fa-edit"></i>
                                </Button>
                                <Button 
                                  @handle:click="removerResponsavel(responsavel.id)" 
                                  color="danger" 
                                  size="small" 
                                  :fill="true"
                                  :disabled="excluindoResponsavel === responsavel.id"
                                  :text="excluindoResponsavel === responsavel.id ? 'Excluindo...' : ''"
                                  :data-testid="`btn-remover-responsavel-${responsavel.id}`">
                                  <i class="fa fa-trash" v-if="excluindoResponsavel !== responsavel.id"></i>
                                  <i class="fa fa-spinner fa-spin" v-if="excluindoResponsavel === responsavel.id"></i>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Paginação -->
                        <div v-if="totalPaginas > 1" class="pagination-container">
                          <div class="pagination-info">
                            <span class="text-muted">
                              Mostrando {{ (paginacaoResponsaveis.paginaAtual - 1) * paginacaoResponsaveis.itensPorPagina + 1 }} 
                              a {{ Math.min(paginacaoResponsaveis.paginaAtual * paginacaoResponsaveis.itensPorPagina, responsaveisFiltrados.length) }} 
                              de {{ responsaveisFiltrados.length }} responsáveis
                            </span>
                          </div>
                          <div class="pagination-controls">
                            <Button 
                              @handle:click="mudarPagina(paginacaoResponsaveis.paginaAtual - 1)"
                              :disabled="paginacaoResponsaveis.paginaAtual === 1"
                              color="secondary"
                              size="small"
                              text="">
                              <i class="fa fa-chevron-left"></i>
                            </Button>
                            
                            <span v-for="pagina in totalPaginas" :key="pagina">
                              <Button 
                                v-if="pagina === paginacaoResponsaveis.paginaAtual"
                                @handle:click="mudarPagina(pagina)"
                                color="primary"
                                size="small"
                                :text="pagina.toString()">
                              </Button>
                              <Button 
                                v-else-if="Math.abs(pagina - paginacaoResponsaveis.paginaAtual) <= 2 || pagina === 1 || pagina === totalPaginas"
                                @handle:click="mudarPagina(pagina)"
                                color="secondary"
                                :fill="false"
                                size="small"
                                :text="pagina.toString()">
                              </Button>
                              <span v-else-if="pagina === paginacaoResponsaveis.paginaAtual - 3 || pagina === paginacaoResponsaveis.paginaAtual + 3" class="pagination-dots">...</span>
                            </span>
                            
                            <Button 
                              @handle:click="mudarPagina(paginacaoResponsaveis.paginaAtual + 1)"
                              :disabled="paginacaoResponsaveis.paginaAtual === totalPaginas"
                              color="secondary"
                              size="small"
                              text="">
                              <i class="fa fa-chevron-right"></i>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Seção de Teste de Alerta (Otimizada para Mobile) -->
                    <div class="telegram-section telegram-test-alert-section" data-testid="test-alert-section">
                      <div class="telegram-test-alert">
                        <div class="test-alert-header">
                          <h6>Testar Envio de Alerta</h6>
                          <p class="text-muted">Envie um alerta de teste para todos os responsáveis</p>
                        </div>
                        <div class="test-alert-form-container">
                          <div class="test-alert-form">
                            <Input
                              legend="Mensagem de teste"
                              :modelValue="mensagemTeste"
                              @update:modelValue="$event => mensagemTeste = $event"
                              placeholder="Digite uma mensagem de teste"
                              type="text"
                              class="test-alert-input"
                              data-testid="input-mensagem-teste"
                            />
                            <Button 
                              @handle:click="enviarAlertaTeste" 
                              text="Enviar Teste" 
                              color="success"
                              class="test-alert-button mobile-friendly-button"
                              :disabled="!mensagemTeste || enviandoTeste"
                              data-testid="btn-enviar-teste-alerta">
                              <i class="fa fa-paper-plane" v-if="!enviandoTeste"></i>
                              <i class="fa fa-spinner fa-spin" v-else></i>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    
                  </div>
                </Accordion>

              </Accordion>
          
        </div>
      </div>
    </div>

    <!-- Modal para Responsável -->
    <div v-if="modalResponsavel.show" class="modal-overlay" @click="modalResponsavel.show = false" data-testid="modal-responsavel-overlay">
      <div class="modal-content" @click.stop data-testid="modal-responsavel-content">
        <div class="modal-header">
          <h5 data-testid="modal-responsavel-titulo">{{ modalResponsavel.editando ? 'Editar' : 'Adicionar' }} Responsável</h5>
          <button class="modal-close" @click="modalResponsavel.show = false" data-testid="btn-fechar-modal">×</button>
        </div>
        <div class="modal-body">
          <Input
            legend="Nome do responsável"
            :modelValue="modalResponsavel.dados.nome"
            @update:modelValue="$event => modalResponsavel.dados.nome = $event"
            placeholder="Digite o nome completo"
            type="text"
            data-testid="input-nome-responsavel"
          />
          <Input
            legend="Número de telefone"
            :modelValue="modalResponsavel.dados.numero_telefone"
            @update:modelValue="$event => modalResponsavel.dados.numero_telefone = $event"
            placeholder="(85) 99999-9999"
            type="text"
            data-testid="input-telefone-responsavel"
            v-mask="'(##) #####-####'"
          />
          <Input
            legend="Username do Telegram"
            :modelValue="modalResponsavel.dados.telegram_username"
            @update:modelValue="$event => modalResponsavel.dados.telegram_username = $event"
            placeholder="Exemplo: @userTeste10"
            type="text"
            data-testid="input-telegram-username"
            required
          >
            <template v-slot:append>
              <i class="fa fa-telegram text-primary"></i>
            </template>
          </Input>
          <Input
            legend="E-mail"
            :modelValue="modalResponsavel.dados.email"
            @update:modelValue="$event => modalResponsavel.dados.email = $event"
            placeholder="email@dominio.com"
            type="email"
            data-testid="input-email-responsavel"
          />
          <div class="form-group custom-switch-container">
            <div class="custom-control custom-switch">
              <input 
                type="checkbox" 
                class="custom-control-input" 
                id="responsavelAtivo" 
                v-model="modalResponsavel.dados.ativo"
                data-testid="checkbox-responsavel-ativo"
              >
              <label class="custom-control-label" for="responsavelAtivo" data-testid="label-responsavel-ativo">
                {{ modalResponsavel.dados.ativo ? 'Ativo' : 'Inativo' }}
              </label>
            </div>
            <div v-if="!modalResponsavel.dados.ativo && !modalResponsavel.editando" class="alert alert-warning mt-2 mb-0" style="font-size: 13px; padding: 12px 14px; line-height: 1.6;" data-testid="aviso-responsavel-inativo">
              <div style="display: flex; align-items: flex-start; gap: 10px;">
                <i class="fa fa-exclamation-triangle" style="margin-top: 3px; flex-shrink: 0; font-size: 16px;"></i>
                <div style="flex: 1; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
                  <strong style="display: block; margin-bottom: 4px;">Atenção:</strong>
                  <span style="display: block;">Responsáveis cadastrados como <strong>inativos</strong> não receberão o e-mail com o link do grupo do Telegram e não terão permissão para acessar ou entrar no grupo.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <Button 
            @handle:click="modalResponsavel.show = false" 
            text="Cancelar" 
            color="secondary"
            data-testid="btn-cancelar-responsavel"
          />
          <Button 
            @handle:click="salvarResponsavel" 
            :text="(modalResponsavel.editando ? 'Atualizar' : 'Salvar') + (salvandoResponsavel ? '...' : '')" 
            color="primary"
            :disabled="salvandoResponsavel || !modalResponsavel.dados.nome || !modalResponsavel.dados.numero_telefone || !modalResponsavel.dados.telegram_username || !modalResponsavel.dados.email"
            data-testid="btn-salvar-responsavel"
          />
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import Button from '../../core/Button.vue'
import Input from '../../core/Input.vue'
import Select from '../../core/Select.vue'
import Accordion from '../../core/Accordion.vue'
import LoadingSpinner from '../../core/LoadingSpinner.vue'
// import PainelAlertas from './PainelAlertas.vue' // Movido para Avisos

export default {
  name: 'Gerenciamento',
  components: {
    Button,
    Input,
    Select,
    Accordion,
    LoadingSpinner
    // PainelAlertas removido - movido para Avisos
  },
  data() {
    return {
      schema: '',
      schemaVehicle: '',
      vehicleOptions: [],
      schemaOptionAdditional: [],
      schemaOptions: [],
      loading: false,
      sections: {
        recognize: {
          active: true,
          percentage: '',
          originalConfig: {},
          changed: false,
          saveStatus: false
        },
        ocr: {
          active: true,
          originalConfig: {},
          changed: false,
          saveStatus: false
        },
        vehicle: {
          active_ignition: true,
          active_gps: true,
          gps_interval: '',
          tolerance_initial: '',
          tolerance_final: '',
          originalConfig: {},
          changed: false,
          saveStatus: false
        },
        teorica: {
          agrupamentos: [],
          changed: false
        }
      },
      agrupamentos: [],
      novoAgrupamento: '',
      odometer: {
        vehicle: '',
        value: '',
        originalConfig: {},
        changed: false,
        saveStatus: false
      },
      telegram: {
        changed: false
      },
      responsaveis: [],
      responsaveisFiltrados: [],
      buscaResponsaveis: '',
      carregandoResponsaveis: false,
      estatisticas: {
        total_cadastrados: 0,
        total_conectados: 0,
        lista_conectados: []
      },
      cacheResponsaveis: {
        dados: null,
        timestamp: null,
        validade: 30000 // 30 segundos
      },
      paginacaoResponsaveis: {
        paginaAtual: 1,
        itensPorPagina: 5,
        total: 0
      },
      statusConexao: null,
      testando: false,
      enviandoTeste: false,
      mensagemTeste: '',
      infoGrupoTelegram: {
        nome: '',
        link: '',
        id: ''
      },
      modalResponsavel: {
        show: false,
        editando: false,
        dados: {
          id: null,
          nome: '',
          numero_telefone: '',
          email: '',
          ativo: true
        }
      },
      salvandoResponsavel: false,
      excluindoResponsavel: null
    };
  },
  computed: {
    hasAnyChanges() {
      return Object.keys(this.sections).some(section => this.sections[section].changed);
    },
    pendingChangesCount() {
      return Object.keys(this.sections).filter(section => this.sections[section].changed).length;
    },
    selectedCfcName() {
      if (!this.schema) return '';
      if (this.schema === 'all') return 'Todas as autoescolas';
      const selectedCfc = this.schemaOptions.find(option => option.value === this.schema);
      return selectedCfc ? selectedCfc.text : '';
    },
    responsaveisPaginados() {
      const inicio = (this.paginacaoResponsaveis.paginaAtual - 1) * this.paginacaoResponsaveis.itensPorPagina;
      const fim = inicio + this.paginacaoResponsaveis.itensPorPagina;
      return this.responsaveisFiltrados.slice(inicio, fim);
    },
    totalPaginas() {
      return Math.ceil(this.responsaveisFiltrados.length / this.paginacaoResponsaveis.itensPorPagina);
    }
  },
  methods: {
    adicionarAgrupamento() {
      if (this.novoAgrupamento && !this.agrupamentos.includes(this.novoAgrupamento)) {
        this.agrupamentos.push(this.novoAgrupamento);
        this.sections.teorica.agrupamentos = [...this.agrupamentos];
        this.sections.teorica.changed = true;
        this.novoAgrupamento = '';
      }
    },
    removerAgrupamento(idx) {
      this.agrupamentos.splice(idx, 1);
      this.sections.teorica.agrupamentos = [...this.agrupamentos];
      this.sections.teorica.changed = true;
    },
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola`)
        .then((res) => {
          this.schemaOptionAdditional = res.body.dados.map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
          this.schemaOptions = res.body.dados.map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
          this.schemaOptions.unshift({ value: 'all', text: 'Todas as autoescolas' })
        })
        .catch((error) => {
          this.$toasted.show('Erro:' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async initOriginalConfigs(schema) {
      try {
        // Fazer a requisição para buscar as configurações da API
        let response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/system-settings?schema=${schema}`);
        
        // Verificar se a resposta contém dados válidos
        if (response.body && response.body.data && response.body.data.configurations) {
          const config = response.body.data.configurations;
          
          // Definir valores iniciais baseados na resposta da API
          // Reconhecimento Facial
          this.sections.recognize.active = config.recognize.active || false;
          this.sections.recognize.percentage = config.recognize.percentage || '';
          
          // OCR
          this.sections.ocr.active = config.ocr.active || false;
          
          // Veículo
          this.sections.vehicle.active_ignition = config.vehicle.active_ignition || false;
          this.sections.vehicle.active_gps = config.vehicle.active_gps || false;
          this.sections.vehicle.gps_interval = config.vehicle.gps_interval || '';
          this.sections.vehicle.tolerance_initial = config.vehicle.tolerance_initial || '';
          this.sections.vehicle.tolerance_final = config.vehicle.tolerance_final || '';
          
          // Definir as configurações originais com os valores carregados
          this.sections.recognize.originalConfig = {
            active: this.sections.recognize.active,
            percentage: this.sections.recognize.percentage
          };
          
          this.sections.ocr.originalConfig = {
            active: this.sections.ocr.active
          };
          
          this.sections.vehicle.originalConfig = {
            active_ignition: this.sections.vehicle.active_ignition,
            active_gps: this.sections.vehicle.active_gps,
            gps_interval: this.sections.vehicle.gps_interval,
            tolerance_initial: this.sections.vehicle.tolerance_initial,
            tolerance_final: this.sections.vehicle.tolerance_final
          };
          
          this.sections.teorica.originalConfig = {
            agrupamentos: this.sections.teorica.agrupamentos
          };
          
          // Resetar status de mudanças
          this.sections.recognize.changed = false;
          this.sections.ocr.changed = false;
          this.sections.vehicle.changed = false;
          
        } else {
          // Se não houver dados, usar valores padrão
          this.setDefaultValues();
        }
        
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        this.$toasted.show('Erro ao carregar configurações: ' + error.body.mensagem, {
          position: 'top-right',
          type: 'error'
        });
        
        // Em caso de erro, usar valores padrão
        this.setDefaultValues();
      }
    },

    // Método auxiliar para definir valores padrão
    setDefaultValues() {
      // Valores padrão
      this.sections.recognize.active = true;
      this.sections.recognize.percentage = '';
      this.sections.ocr.active = true;
      this.sections.vehicle.active_ignition = true;
      this.sections.vehicle.active_gps = true;
      this.sections.vehicle.gps_interval = '';
      this.sections.vehicle.tolerance_initial = '';
      this.sections.vehicle.tolerance_final = '';
      
      // Definir configurações originais
      this.sections.recognize.originalConfig = {
        active: this.sections.recognize.active,
        percentage: this.sections.recognize.percentage
      };
      
      this.sections.ocr.originalConfig = {
        active: this.sections.ocr.active
      };
      
      this.sections.vehicle.originalConfig = {
        active_ignition: this.sections.vehicle.active_ignition,
        active_gps: this.sections.vehicle.active_gps,
        gps_interval: this.sections.vehicle.gps_interval,
        tolerance_initial: this.sections.vehicle.tolerance_initial,
        tolerance_final: this.sections.vehicle.tolerance_final
      };

      this.sections.teorica.originalConfig = {
        agrupamentos: []
      };
      
      // Resetar status de mudanças
      this.sections.recognize.changed = false;
      this.sections.ocr.changed = false;
      this.sections.vehicle.changed = false;
    },
    async saveSection(section) {
      const payload = {
        schema: this.schema,
        configurations: {
          recognize: {
            active: this.sections.recognize.active,
            percentage: this.sections.recognize.percentage
          },
          ocr: {
            active: this.sections.ocr.active
          },
          vehicle: {
            active_ignition: this.sections.vehicle.active_ignition,
            active_gps: this.sections.vehicle.active_gps,
            gps_interval: this.sections.vehicle.gps_interval,
            tolerance_initial: this.sections.vehicle.tolerance_initial,
            tolerance_final: this.sections.vehicle.tolerance_final
          },
          teorica: {
            agrupamentos: this.sections.teorica.agrupamentos
          }
        }
      }
      try {
        await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/system-settings`, payload);
        await this.initOriginalConfigs(this.schema);
        
        this.sections[section].changed = false;
        this.sections[section].saveStatus = true;
        
        this.$toasted.show('Configurações salvas com sucesso!', {
          position: 'top-right',
          type: 'success'
        });
        
      } catch (error) {
        this.$toasted.show('Erro ao salvar configurações: ' + (!!error.body ? error.body.mensagem : error.message), {
          position: 'top-right',
          type: 'error'
        });
      }
    },
    async saveHodometro() {
      const payload = {
        schema: this.schemaVehicle,
        veiculo: this.odometer.vehicle,
        hodometro: this.odometer.value
      }
      try {
        await this.$http.patch(`${process.env.VUE_APP_URL_API_V2}/veiculo/set-odometer`, payload);
        
        this.odometer.value = ''
        this.$toasted.show('Hodômetro atualizado com sucesso!', {
          position: 'top-right',
          type: 'success'
        });
        
      } catch (error) {
        this.$toasted.show('Erro ao atualizar hodômetro: ' + (!!error.body ? error.body.mensagem : error.message), {
          position: 'top-right',
          type: 'error'
        });
      }
    },
    checkChanges(section) {
      if (section === 'recognize') {
        const currentActive = this.sections.recognize.active;
        const currentPercentage = this.sections.recognize.percentage;
        const originalActive = this.sections.recognize.originalConfig.active;
        const originalPercentage = this.sections.recognize.originalConfig.percentage;
        
        this.sections.recognize.changed = currentActive !== originalActive || currentPercentage !== originalPercentage;
      } else if (section === 'ocr') {
        const currentActive = this.sections.ocr.active;
        const originalActive = this.sections.ocr.originalConfig.active;
        
        this.sections.ocr.changed = currentActive !== originalActive;
      } else if (section === 'vehicle') {
        const currentActiveIgnition = this.sections.vehicle.active_ignition;
        const originalActiveIgnition = this.sections.vehicle.originalConfig.active_ignition;
        const currentActiveGps = this.sections.vehicle.active_gps;
        const originalActiveGps = this.sections.vehicle.originalConfig.active_gps;
        const currentGpsInterval = this.sections.vehicle.gps_interval;
        const originalGpsInterval = this.sections.vehicle.originalConfig.gps_interval;
        const currentToleranceInitial = this.sections.vehicle.tolerance_initial;
        const originalToleranceInitial = this.sections.vehicle.originalConfig.tolerance_initial;
        const currentToleranceFinal = this.sections.vehicle.tolerance_final;
        const originalToleranceFinal = this.sections.vehicle.originalConfig.tolerance_final;
        
        this.sections.vehicle.changed = 
          currentActiveIgnition !== originalActiveIgnition || 
          currentActiveGps !== originalActiveGps || 
          currentGpsInterval !== originalGpsInterval || 
          currentToleranceInitial !== originalToleranceInitial || 
          currentToleranceFinal !== originalToleranceFinal;
      }
    },
    applyAllChanges() {
      Object.keys(this.sections).forEach(section => {
        if (this.sections[section].changed) {
          this.saveSection(section);
        }
      });
    },
    async resetAll() {
      if (confirm('Tem certeza que deseja restaurar todas as configurações para os valores padrão?')) {
        await this.$http.delete(`${process.env.VUE_APP_URL_API_V2}/system-settings`)
        .then(() => {
          this.$toasted.show('Configurações restauradas com sucesso!', {
            position: 'top-right',
            type: 'success'
          });
          // Restaurar para valores padrão de todas as seções
          this.sections.recognize.active = true;
          this.sections.recognize.percentage = '';
          this.checkChanges('recognize');
          
          this.sections.ocr.active = true;
          this.checkChanges('ocr');
          
          this.sections.vehicle.active_ignition = true;
          this.sections.vehicle.active_gps = true;
          this.sections.vehicle.gps_interval = '';
          this.sections.vehicle.tolerance_initial = '';
          this.sections.vehicle.tolerance_final = '';
          this.checkChanges('vehicle');
        }).catch((error) => {
          this.$toasted.show('Erro ao restaurar configurações: ' + (!!error.body ? error.body.mensagem : error.message), {
            position: 'top-right',
            type: 'error'
          });
        });
      }
    },
    async fetchVehicles() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/veiculo?schema=${this.schemaVehicle}`
      ).then((res) => {
        if (res.body && res.body.length > 0) {
          this.vehicleOptions = res.body.map(vehicle => ({
            value: vehicle.id_veiculo,
            text: `${vehicle.placa}`
          }));
        } else {
          console.log('Veículos carregados:', res.body.dados);
          this.vehicleOptions = [];
        }
      }).catch((error) => {
        this.$toasted.show('Erro ao buscar veículos: ' + error.body.mensagem, {
          position: 'top-right',
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    async carregarResponsaveis(forceReload = false) {
      // Verifica cache se não for reload forçado
      if (!forceReload && this.verificarCache()) {
        return;
      }

      try {
        this.carregandoResponsaveis = true;
        
        console.log('🔄 Carregando responsáveis...', {
          url: `${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/responsaveis`,
          force_reload: forceReload
        });
        
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/responsaveis`);
        
        console.log('📡 Resposta da API:', response);
        
        if (response.body && response.body.sucesso) {
          // Atualiza dados imediatamente
          this.responsaveis = response.body.dados || [];
          
          console.log('✅ Responsáveis carregados:', {
            total: this.responsaveis.length,
            dados: this.responsaveis
          });
          
          // Atualiza cache
          this.atualizarCache(response.body);
          
          // Atualiza estatísticas
          if (response.body.estatisticas) {
            this.estatisticas = response.body.estatisticas;
            console.log(`📊 Estatísticas: ${this.estatisticas.total_cadastrados} responsáveis, ${this.estatisticas.total_conectados} conectados`);
          }
          
          // Filtra e força atualização da interface
          this.filtrarResponsaveis();
          this.$nextTick(() => {
            this.$forceUpdate();
          });
          
        } else {
          console.warn('❌ API retornou sucesso=false:', response.body);
          this.responsaveis = [];
          this.responsaveisFiltrados = [];
        }
        
      } catch (error) {
        const errMsg = (error && error.body && (error.body.mensagem || error.body.message)) || error.message || 'Falha desconhecida';
        console.error('❌ Erro ao carregar responsáveis:', errMsg, error);
        this.responsaveis = [];
        this.responsaveisFiltrados = [];
        this.$toasted.show('Erro ao carregar responsáveis: ' + errMsg, {
          position: 'top-right',
          type: 'error',
          duration: 5000
        });
      } finally {
        this.carregandoResponsaveis = false;
      }
    },
    
    verificarCache() {
      if (!this.cacheResponsaveis.dados || !this.cacheResponsaveis.timestamp) {
        return false;
      }
      
      const agora = Date.now();
      const tempoDecorrido = agora - this.cacheResponsaveis.timestamp;
      
      if (tempoDecorrido < this.cacheResponsaveis.validade) {
        // Cache válido, usa dados em cache
        this.responsaveis = this.cacheResponsaveis.dados.dados || [];
        this.estatisticas = this.cacheResponsaveis.dados.estatisticas || this.estatisticas;
        this.filtrarResponsaveis();
        return true;
      }
      
      return false;
    },
    
    atualizarCache(dados) {
      this.cacheResponsaveis = {
        dados: dados,
        timestamp: Date.now(),
        validade: 30000
      };
    },
    
    invalidarCache() {
      this.cacheResponsaveis = {
        dados: null,
        timestamp: null,
        validade: 30000
      };
    },
    filtrarResponsaveis() {
      if (!this.responsaveis || this.responsaveis.length === 0) {
        this.responsaveisFiltrados = [];
        this.paginacaoResponsaveis.total = 0;
        this.paginacaoResponsaveis.paginaAtual = 1;
        return;
      }

      const busca = this.buscaResponsaveis.toLowerCase().trim();
      
      if (!busca) {
        // Se não há busca, mostra todos
        this.responsaveisFiltrados = [...this.responsaveis];
      } else {
        // Filtra com cache para melhor performance
        this.responsaveisFiltrados = this.responsaveis.filter(responsavel => {
          const nome = responsavel.nome.toLowerCase();
          const telefone = responsavel.numero_telefone.replace(/\D/g, '');
          const buscaLimpa = busca.replace(/\D/g, '');
          
          return nome.includes(busca) || 
                 telefone.includes(buscaLimpa) ||
                 responsavel.numero_telefone.includes(busca);
        });
      }
      
      // Reset pagination when filtering
      this.paginacaoResponsaveis.paginaAtual = 1;
      this.paginacaoResponsaveis.total = this.responsaveisFiltrados.length;
    },
    
    // Versão com debounce para busca em tempo real
    filtrarResponsaveisDebounced() {
      clearTimeout(this._filtroTimeout);
      this._filtroTimeout = setTimeout(() => {
        this.filtrarResponsaveis();
      }, 300);
    },
    mudarPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacaoResponsaveis.paginaAtual = pagina;
      }
    },
    async testarConexao() {
      this.testando = true;
      this.statusConexao = null;
      
      try {
        const response = await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/teste-conexao`);
        this.statusConexao = response.body;
        
        // Mostra detalhes se disponíveis
        if (response.body.detalhes) {
          const detalhes = response.body.detalhes;
          let mensagemDetalhada = '';
          
          if (detalhes.bot_nome) {
            mensagemDetalhada += `🤖 Bot: ${detalhes.bot_nome}`;
            if (detalhes.bot_username && detalhes.bot_username !== 'N/A') {
              mensagemDetalhada += ` (@${detalhes.bot_username})`;
            }
          }
          
          if (detalhes.observacao) {
            mensagemDetalhada += `\n💡 ${detalhes.observacao}`;
          }
          
          if (detalhes.erro) {
            mensagemDetalhada += `\n❌ ${detalhes.erro}`;
          }
          
          if (mensagemDetalhada) {
            setTimeout(() => {
              this.$toasted.show(mensagemDetalhada, {
                position: 'top-right',
                type: response.body.sucesso ? 'info' : 'warning',
                duration: 6000
              });
            }, 1000);
          }
        }
      } catch (error) {
        this.statusConexao = {
          sucesso: false,
          mensagem: error.body && error.body.mensagem || 'Erro na conexão'
        };
      } finally {
        this.testando = false;
      }
    },
    // Métodos para o grupo Telegram
    async abrirGrupoTelegram() {
      try {
        // Busca o link do grupo via API para usar sempre o mais atualizado
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/grupo-info`);
        
        if (response.body.sucesso && response.body.dados.link) {
          window.open(response.body.dados.link, '_blank');
        } else {
          // Fallback para link padrão
          window.open('https://t.me/+1PUXjxtaDugzZGMx', '_blank');
        }
      } catch (error) {
        // Em caso de erro, usa link padrão
        window.open('https://t.me/+1PUXjxtaDugzZGMx', '_blank');
      }
    },
    async obterInfoGrupo() {
      try {
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/grupo-info`);
        
        if (response.body.sucesso) {
          const info = response.body.dados;
          
          // Atualiza a variável local para uso no template
          this.infoGrupoTelegram = {
            nome: info.nome,
            link: info.link,
            id: info.id
          };
          
          let mensagem = `📱 ${info.nome}\n\n`;
          mensagem += `🔗 Link: ${info.link}\n`;
          mensagem += `📋 Tipo: ${info.tipo}\n`;
          mensagem += `📄 Descrição: ${info.descricao}\n\n`;
          
          if (info.instrucoes && info.instrucoes.length > 0) {
            mensagem += `📋 Instruções:\n`;
            info.instrucoes.forEach((instrucao, index) => {
              mensagem += `${instrucao}\n`;
            });
          }
          
          this.$toasted.show(mensagem, {
            position: 'top-right',
            type: 'info',
            duration: 8000
          });
        }
      } catch (error) {
        this.$toasted.show('Erro ao obter informações do grupo: ' + (error.body && error.body.mensagem || error.message), {
          position: 'top-right',
          type: 'error'
        });
      }
    },
    async carregarInfoGrupoTelegram() {
      try {
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/grupo-info`);
        
        if (response.body.sucesso) {
          const info = response.body.dados;
          this.infoGrupoTelegram = {
            nome: info.nome,
            link: info.link,
            id: info.id
          };
        }
      } catch (error) {
        // Em caso de erro, mantém valores padrão
        this.infoGrupoTelegram = {
          nome: 'Notificações - LOGS SISTEMA NOVO CFC',
          link: 'https://t.me/+1PUXjxtaDugzZGMx',
          id: '-1002495993765'
        };
      }
    },
    async enviarAlertaTeste() {
      this.enviandoTeste = true;
      
      try {
        const response = await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/teste-alerta`, {
          mensagem: this.mensagemTeste
        });
        
        // Mostra a mensagem principal
        this.$toasted.show(response.body.mensagem, {
          position: 'top-right',
          type: response.body.sucesso ? 'success' : 'error',
          duration: 5000
        });
        
        // Se há detalhes, mostra informações adicionais
        if (response.body.detalhes) {
          const detalhes = response.body.detalhes;
          let mensagemDetalhada = '';
          
          if (typeof detalhes === 'object') {
            if (detalhes.total_responsaveis !== undefined) {
              mensagemDetalhada += `📊 Responsáveis: ${detalhes.total_responsaveis} cadastrados`;
              if (detalhes.com_chat_id !== undefined) {
                mensagemDetalhada += `, ${detalhes.com_chat_id} conectados`;
              }
            }
            
            if (detalhes.enviados !== undefined) {
              mensagemDetalhada += `\n✅ Enviados: ${detalhes.enviados}`;
            }
            
            if (detalhes.falhas && detalhes.falhas > 0) {
              mensagemDetalhada += `\n❌ Falhas: ${detalhes.falhas}`;
            }
            
            if (detalhes.observacao) {
              mensagemDetalhada += `\n💡 ${detalhes.observacao}`;
            }
          } else {
            mensagemDetalhada = detalhes;
          }
          
          if (mensagemDetalhada) {
            setTimeout(() => {
              this.$toasted.show(mensagemDetalhada, {
                position: 'top-right',
                type: 'info',
                duration: 8000
              });
            }, 1500);
          }
        }
        
        if (response.body.sucesso) {
          this.mensagemTeste = '';
        }
      } catch (error) {
        this.tratarErroEnvioTeste(error);
      } finally {
        this.enviandoTeste = false;
      }
    },
    
    tratarErroEnvioTeste(error) {
      let mensagemPrincipal = 'Erro ao enviar teste de alerta';
      let mensagemDetalhada = '';
      let tipoToast = 'error';
      let duracao = 6000;
      
      // Verifica se é erro HTTP estruturado
      if (error.body && error.body.mensagem) {
        mensagemPrincipal = error.body.mensagem;
        
        // Processa detalhes se existirem
        if (error.body.detalhes) {
          const detalhes = error.body.detalhes;
          
          // Monta mensagem detalhada baseada no tipo de erro
          if (detalhes.codigo_erro) {
            mensagemDetalhada += `\n🔍 Código: ${detalhes.codigo_erro}`;
          }
          
          if (detalhes.tipo_erro) {
            mensagemDetalhada += `\n⚠️ Tipo: ${detalhes.tipo_erro}`;
          }
          
          if (detalhes.acao_necessaria) {
            mensagemDetalhada += `\n💡 Ação: ${detalhes.acao_necessaria}`;
          }
          
          if (detalhes.erro_tecnico && this.isDebugMode()) {
            mensagemDetalhada += `\n🔧 Debug: ${detalhes.erro_tecnico}`;
          }
          
          // Tipos específicos de erro
          if (detalhes.tipo_erro === 'ConnectException') {
            tipoToast = 'warning';
            mensagemDetalhada += '\n🌐 Verifique sua conexão com a internet';
          }
          
          if (detalhes.tipo_erro === 'ClientException') {
            mensagemDetalhada += '\n🤖 Verifique a configuração do bot Telegram';
          }
          
          if (detalhes.codigo_erro && detalhes.codigo_erro.includes('CRITICAL')) {
            tipoToast = 'error';
            duracao = 10000;
            mensagemDetalhada += '\n🚨 Entre em contato com o suporte técnico';
          }
        }
        
        // Status HTTP específicos
        if (error.status) {
          switch (error.status) {
            case 422:
              tipoToast = 'warning';
              duracao = 5000;
              break;
            case 500:
              mensagemDetalhada += '\n⚙️ Erro interno do servidor';
              break;
            case 503:
              mensagemDetalhada += '\n⏰ Serviço temporariamente indisponível';
              break;
          }
        }
        
      } else if (error.message) {
        // Erro de rede/cliente
        mensagemPrincipal = 'Falha na comunicação com o servidor';
        mensagemDetalhada = `\n🔗 ${error.message}`;
        tipoToast = 'warning';
      }
      
      // Exibe mensagem principal
      this.$toasted.show(mensagemPrincipal, {
        position: 'top-right',
        type: tipoToast,
        duration: duracao
      });
      
      // Exibe detalhes se existirem
      if (mensagemDetalhada) {
        setTimeout(() => {
          this.$toasted.show(mensagemDetalhada, {
            position: 'top-right',
            type: 'info',
            duration: duracao + 2000
          });
        }, 1500);
      }
      
      // Log no console para debug
      console.error('Erro detalhado do teste de alerta:', {
        error: error,
        body: error.body,
        status: error.status,
        timestamp: new Date().toISOString()
      });
    },
    
    isDebugMode() {
      // Retorna true se estiver em modo debug (desenvolvimento)
      return process.env.NODE_ENV === 'development' || 
             window.location.hostname === 'localhost' ||
             localStorage.getItem('debug_mode') === 'true';
    },
    
    atualizarEstatisticas() {
      // Atualiza estatísticas localmente sem fazer nova requisição
      this.estatisticas.total_cadastrados = this.responsaveis.length;
      this.estatisticas.total_conectados = this.responsaveis.filter(r => 
        r.ativo && r.chat_id && r.chat_id !== 'N/A' && r.chat_id !== null
      ).length;
      
      this.estatisticas.lista_conectados = this.responsaveis
        .filter(r => r.ativo && r.chat_id && r.chat_id !== 'N/A' && r.chat_id !== null)
        .map(r => ({
          nome: r.nome,
          chat_id: r.chat_id
        }));
    },
    

    
    abrirModalResponsavel() {
      this.modalResponsavel = {
        show: true,
        editando: false,
        dados: {
          id: null,
          nome: '',
          numero_telefone: '',
          telegram_username: '',
          email: '',
          ativo: true
        }
      };
    },
    editarResponsavel(responsavel) {
      this.modalResponsavel = {
        show: true,
        editando: true,
        dados: { ...responsavel }
      };
    },
    async salvarResponsavel() {
      try {
        this.salvandoResponsavel = true;
        const payload = {
          nome: this.modalResponsavel.dados.nome,
          numero_telefone: this.modalResponsavel.dados.numero_telefone,
          telegram_username: this.modalResponsavel.dados.telegram_username || null,
          email: this.modalResponsavel.dados.email,
          ativo: this.modalResponsavel.dados.ativo
        };

        let response;
        if (this.modalResponsavel.editando) {
          response = await this.$http.put(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/responsaveis/${this.modalResponsavel.dados.id}`, payload);
        } else {
          response = await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/responsaveis`, payload);
        }

        // Atualização otimística - atualiza a lista sem recarregar tudo
        if (response.body.sucesso) {
          if (this.modalResponsavel.editando) {
            // Edição - atualiza item existente
            const index = this.responsaveis.findIndex(r => r.id === this.modalResponsavel.dados.id);
            if (index !== -1) {
              this.$set(this.responsaveis, index, {
                ...this.responsaveis[index],
                ...payload,
                id: this.modalResponsavel.dados.id
              });
            }
          } else {
            // Criação - adiciona novo item
            const novoResponsavel = response.body.dados || {
              ...payload,
              id: Date.now(), // Fallback para ID temporário
              chat_id: null
            };
            this.responsaveis.unshift(novoResponsavel);
          }
          
          // Atualiza filtros e estatísticas instantaneamente
          this.filtrarResponsaveis();
          this.atualizarEstatisticas();
          
          // Invalida cache para próxima requisição
          this.invalidarCache();
        }

        // Mostra resultado
        let mensagem = `Responsável ${this.modalResponsavel.editando ? 'atualizado' : 'cadastrado'} com sucesso!`;
        
        if (!this.modalResponsavel.editando && response.body.adicao_automatica) {
          const adicao = response.body.adicao_automatica;
          if (adicao.sucesso) {
            if (adicao.metodo === 'madelineproto_direto') {
              mensagem += '\n🚀 ADICIONADO AUTOMATICAMENTE via MadelineProto - 100% automático!';
            } else if (adicao.metodo === 'madelineproto_link_enviado') {
              mensagem += '\n📨 Link de convite enviado diretamente via Telegram!';
            } else if (adicao.metodo === 'bot_api_interacao') {
              mensagem += '\n🤖 Adicionado via interação com bot - sucesso!';
            } else if (adicao.metodo === 'adicao_automatica_completa') {
              mensagem += '\n🎉 Responsável adicionado automaticamente ao grupo do Telegram!';
            } else if (adicao.metodo === 'interacao_sem_grupo') {
              mensagem += '\n✅ Usuário conectado, mas aguardando permissão para entrar no grupo.';
            } else if (adicao.metodo === 'cadastro_pendente_interacao') {
              mensagem += '\n📱 Aguardando primeira interação com o bot para adicionar automaticamente.';
            } else if (adicao.metodo === 'cadastro_manual_necessario') {
              mensagem += '\n📋 Cadastrado - entrada manual no grupo necessária.';
            } else if (adicao.metodo === 'notificacao_automatica') {
              mensagem += '\n✅ Responsável notificado automaticamente via Telegram!';
            } else if (adicao.metodo === 'cadastro_automatico') {
              mensagem += '\n🎯 Sistema configurado - receberá alertas automaticamente.';
            }
          } else {
            mensagem += '\n⚠️ Erro no cadastro automático: ' + (adicao.mensagem || 'Erro desconhecido');
          }
        }
        
        this.$toasted.show(mensagem, {
          position: 'top-right',
          type: 'success',
          duration: 5000
        });

        this.modalResponsavel.show = false;
        
      } catch (error) {
        console.error('Erro ao salvar responsável:', error);
        this.$toasted.show('Erro ao salvar responsável: ' + (error.body && error.body.mensagem || error.message), {
          position: 'top-right',
          type: 'error',
          duration: 5000
        });
      } finally {
        this.salvandoResponsavel = false;
      }
    },
    async removerResponsavel(id) {
      if (confirm('Tem certeza que deseja remover este responsável?')) {
        this.excluindoResponsavel = id;
        try {
          const response = await this.$http.delete(`${process.env.VUE_APP_URL_API_V2}/monitoramento/alertas/responsaveis/${id}`);
          
          if (response.body.sucesso) {
            // Remove item da lista instantaneamente
            const index = this.responsaveis.findIndex(r => String(r.id) === String(id));
            if (index !== -1) {
              this.responsaveis.splice(index, 1);
            }
            
            // Atualiza filtros e estatísticas
            this.filtrarResponsaveis();
            this.atualizarEstatisticas();
            
            // Invalida cache para próxima requisição
            this.invalidarCache();
            
            // Monta mensagem baseada nos detalhes da remoção
            let mensagem = (response.body && response.body.mensagem) ? response.body.mensagem : 'Responsável removido com sucesso!';
            let tipo = 'success';
            let duracao = 4000;
            
            // Verifica se há detalhes sobre a remoção do Telegram
            if (response.body && response.body.detalhes && response.body.detalhes.telegram) {
              const detalhesTelegram = response.body.detalhes.telegram;
              
              if (detalhesTelegram === 'Removido do grupo') {
                mensagem += ' ✅ Também removido do grupo Telegram.';
              } else if (detalhesTelegram.includes('não estava no grupo')) {
                mensagem += ' ℹ️ Usuário não estava no grupo Telegram.';
              } else if (detalhesTelegram.includes('interagido com o bot')) {
                mensagem += ' ⚠️ Remoção automática do Telegram requer que o usuário tenha conversado com o bot primeiro.';
                tipo = 'warning';
                duracao = 6000;
              } else if (detalhesTelegram.includes('Erro')) {
                mensagem += ' ⚠️ Erro na verificação do grupo Telegram.';
                tipo = 'warning';
              }
            }
            
            this.$toasted.show(mensagem, {
              position: 'top-right',
              type: tipo,
              duration: duracao
            });
          }
          
        } catch (error) {
          console.error('Erro ao remover responsável:', error);
          const errMsg = (error && error.body && (error.body.mensagem || error.body.message)) || error.message || 'Falha desconhecida';
          this.$toasted.show('Erro ao remover responsável: ' + errMsg, {
            position: 'top-right',
            type: 'error',
            duration: 5000
          });
        } finally {
          this.excluindoResponsavel = null;
        }
      }
    },
    formatarTelefone(telefone) {
      if (!telefone) return '';
      
      // Remove todos os caracteres não numéricos
      const numeros = telefone.replace(/\D/g, '');
      
      // Aplica a máscara (##) #####-####
      if (numeros.length === 11) {
        return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
      }
      
      // Se não tem 11 dígitos, retorna como está
      return telefone;
    }
  },
  watch: {
    responsaveis: {
      handler() {
        this.filtrarResponsaveis();
      },
      deep: true
    },
    buscaResponsaveis: {
      handler() {
        this.filtrarResponsaveisDebounced();
      }
    },
    'sections.recognize.active'() {
      this.checkChanges('recognize');
    },
    'sections.recognize.percentage'() {
      this.checkChanges('recognize');
    },
    'sections.ocr.active'() {
      this.checkChanges('ocr');
    },
    'sections.vehicle.active_ignition'() {
      this.checkChanges('vehicle');
    },
    'sections.vehicle.active_gps'() {
      this.checkChanges('vehicle');
    },
    'sections.vehicle.gps_interval'() {
      this.checkChanges('vehicle');
    },
    'sections.vehicle.tolerance_initial'() {
      this.checkChanges('vehicle');
    },
    'sections.vehicle.tolerance_final'() {
      this.checkChanges('vehicle');
    },
    schemaVehicle(newValue) {
      this.loading = true;
      
      // Resetar valor do veículo quando mudar a autoescola
      this.odometer.vehicle = '';
      this.checkChanges('odometer');
      
      // Buscar veículos da autoescola selecionada
      this.fetchVehicles();
    },
    schema(newSchema) {
      this.initOriginalConfigs(newSchema);
    }
  },
  mounted() {
    const adminUser = [
      'demys@fabricainfo.com',
      'nadlo.cavalcante@fabricainfo.com',
      'nicole@monitoramento.com',
      'wylder@monitoramento.com', 
      'brenda.tavares@monitoramento.com'
      
    ];
    if (!adminUser.includes(localStorage.email)) {
      window.history.back();
    }
    this.fetchCfcs();
    this.carregarInfoGrupoTelegram();
    this.carregarResponsaveis();
  }
};
</script>
  
<style lang="scss" scoped>
.config-widget {
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  overflow: visible;
}

.config-widget-header {
  padding: 1.25rem 1.5rem;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 1px solid #eeeeee;
}

.config-widget-content {
  padding: 1.5rem;
  min-height: 60vh;
}

.accordion-container {
  width: 100%;
}

.form-group:not(:last-child) {
  margin-bottom: 1.5rem;
}

.section-description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 20px;
}

.config-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.config-status {
  font-size: 14px;
  margin-left: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.config-status.visible {
  opacity: 1;
}

.changed-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #1C2D5A !important;
  border-radius: 50%;
  margin-left: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.changed-indicator.visible {
  opacity: 1;
}

@keyframes fadeOut {
  0% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}

.fade-out {
  animation: fadeOut 3s forwards;
}

.global-actions {
  background-color: #f8f9fa !important;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #eee;
}

.actions-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.actions-info {
  flex: 1;
}

.actions-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.custom-switch-container {
  margin-top: 15px;
}

.form-group {
  margin-bottom: 20px;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  width: 100%;
  padding: 2rem;
}

.loading-text {
  margin-top: 1rem;
  color: #1C2D5A;
  font-weight: 500;
  font-size: 1rem;
}

.vehicle-settings-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.vehicle-setting-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.vehicle-setting-item {
  display: flex;
  flex-direction: column;
  min-width: 250px;
  flex: 1;
}

.tolerance-section {
  margin-top: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.tolerance-title {
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.tolerance-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.tolerance-input-group {
  min-width: 250px;
  flex: 1;
}

.gps-interval-text {
  margin-top: 0.5rem;
  font-size: 14px;
  color: #6c757d;
}

.odometer-settings-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.odometer-setting-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.odometer-setting-item {
  display: flex;
  flex-direction: column;
  min-width: 250px;
  flex: 1;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.small-note {
  font-size: 12px;
}
.alert {
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  
  &.alert-info {
    background-color: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
    
    .fa-info-circle {
      color: #17a2b8;
    }
  }
}

.me-2 {
  margin-right: 0.5rem;
}

/* Estilos para Alertas Telegram */
.telegram-settings-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.telegram-test-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #eee;
}

.test-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.test-info h6 {
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 14px;
}

.connection-status.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.connection-status.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.responsaveis-section {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
}

/* Estilos para agrupamentos inline */
.agrupamentos-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.agrupamento-tag {
  display: inline-flex;
  align-items: center;
  background-color: #1C2D5A;
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  transition: background-color 0.2s ease;
}

.agrupamento-tag:hover {
  background-color: #2a3f6b;
}

.remove-btn {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.remove-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.agrupamentos-lista h6 {
  margin-bottom: 10px;
  font-weight: 600;
  color: #333;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

.section-header h6 {
  margin: 0;
  font-weight: 600;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  text-align: center;
}

.responsaveis-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px;
}

.responsavel-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

.responsavel-item:last-child {
  border-bottom: none;
}

.responsavel-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.responsavel-info .nome {
  font-weight: 600;
  color: #333;
}

.responsavel-info .telefone {
  color: #666;
  font-size: 14px;
}

.responsavel-info .status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  width: fit-content;
}

.responsavel-info .status.ativo {
  background-color: #d4edda;
  color: #155724;
}

.responsavel-info .status.inativo {
  background-color: #f8d7da;
  color: #721c24;
}

.responsavel-actions {
  display: flex;
  gap: 0.5rem;
}

.telegram-test-alert {
  width: 100%;
  display: block;
}

.test-alert-header h6 {
  margin-bottom: 0.25rem;
  font-weight: 600;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.test-alert-header {
  margin-bottom: 15px;
}

.test-alert-form {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  margin-top: 1rem;
  width: 100%;
  max-width: 100%;
  flex-wrap: wrap;
  box-sizing: border-box;
}

.test-alert-form .input-container {
  flex: 1;
  width: 100%;
}

.test-alert-input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.test-alert-button {
  white-space: nowrap;
  min-width: 100px;
  max-width: 120px;
  flex-shrink: 0;
}

.mobile-friendly-button {
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 16px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Estilos para Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.modal-header h5 {
  margin: 0;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  line-height: 1;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
  
  /* Espaçamento entre campos do formulário */
  & > div {
    margin-bottom: 1.25rem;
  }
  
  /* Espaçamento das legendas dos inputs */
  legend {
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 14px;
    color: #495057;
  }
  
  /* Ajuste do switch de ativo/inativo */
  .custom-switch-container {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

/* Estilos específicos para Alertas Telegram */
.telegram-settings-container {
  width: 100%;
  display: block;
  min-height: 200px; /* Garante altura mínima para o conteúdo */
  overflow-y: auto; /* Permite scroll vertical se necessário */
  max-height: 100vh; /* Limita a altura máxima à altura da viewport */
}

.telegram-section {
  margin-bottom: 25px;
  width: 100%;
  background-color: #fafbfc;
  border-radius: 12px;
  border: 1px solid #f1f3f4;
  padding: 20px;
}

.telegram-test-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.test-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.test-info {
  flex: 1;
  min-width: 200px;
}

.test-info h6 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 5px;
}

.connection-status {
  padding: 12px 16px;
  border-radius: 6px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.connection-status.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.connection-status.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.telegram-test-alert-section {
  margin-top: 25px;
  margin-bottom: 24px;
  position: relative;
  min-height: 200px;
}

.test-alert-form-container {
  position: relative;
  overflow: hidden;
  min-height: 100px;
  padding: 0 5px 15px 5px;
  width: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.section-header h6 {
  color: #495057;
  font-weight: 600;
  margin: 0;
}

.search-container {
  margin: 25px 0;
  padding: 0 5px;
}

.responsaveis-scroll-container {
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
}

.responsaveis-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px;
}

.responsavel-item {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  margin: 6px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.responsavel-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.15);
}

.responsavel-info {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  align-items: center;
}

.responsavel-info .nome {
  font-weight: 600;
  color: #212529;
  font-size: 16px;
}

.responsavel-info .telefone {
  color: #6c757d;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
}

.responsavel-info .status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
}

.responsavel-info .status.ativo {
  color: #28a745;
}

.responsavel-info .status.inativo {
  color: #dc3545;
}



.responsavel-actions {
  display: flex;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

.pagination-container {
  margin-top: 30px;
  padding: 25px 15px 5px;
  border-top: 1px solid #e9ecef;
  background-color: #ffffff;
  border-radius: 8px;
}

.pagination-info {
  text-align: center;
  margin-bottom: 15px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.pagination-dots {
  padding: 8px 4px;
  color: #6c757d;
  font-weight: 500;
}

.empty-state, .loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.empty-state i, .loading-state i {
  margin-bottom: 15px;
}

.loading-state {
  opacity: 0.9;
}

.loading-state i {
  color: #007bff !important;
}

/* Responsividade */
@media (max-width: 992px) {
  .telegram-section {
    padding: 15px;
  }
}

/* Dispositivos móveis */
@media (max-width: 768px) {
  .responsavel-item {
    padding: 14px;
  }
  .responsavel-actions button {
    height: 36px;
  }
  .telegram-settings-container {
    padding: 0;
  }
  
  .test-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .responsavel-item {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .responsavel-info {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
  }
  
  .responsavel-actions {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
  
  .pagination-controls {
    gap: 2px;
  }
  
  .test-alert-form {
    flex-direction: column;
    align-items: stretch;
  }
  
  .telegram-test-alert-section {
    min-height: 180px;
    overflow: visible;
  }
  
  .test-alert-header h6,
  .test-alert-header p {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

/* Dispositivos pequenos (tablets pequenos, etc) */
@media (max-width: 721px) {
  .telegram-section {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  /* Adiciona scroll vertical quando necessário para telas menores */
  .responsaveis-scroll-container {
    max-height: 200px; /* Reduzido para dar mais espaço à seção de teste */
    overflow-y: auto;
    padding-right: 5px; /* Espaço para a barra de rolagem */
  }
  
  /* Estiliza a barra de rolagem para melhor UX */
  .responsaveis-scroll-container::-webkit-scrollbar {
    width: 6px;
  }
  
  .responsaveis-scroll-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .responsaveis-scroll-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
  
  .responsaveis-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  /* Ajusta o espaçamento para telas menores */
  .responsaveis-list {
    gap: 15px;
  }
  
  .responsavel-item {
    padding: 15px;
  }
  
  /* Garante que a seção de teste de alerta seja visível */
  .telegram-test-alert-section {
    margin-top: 15px;
    min-height: 180px;
  }
  
  /* Adiciona scroll horizontal para o formulário se necessário */
  .test-alert-form-container {
    overflow-x: visible;
    padding-bottom: 5px;
  }
  
  /* Estiliza a barra de rolagem horizontal */
  .test-alert-form-container::-webkit-scrollbar {
    height: 6px;
  }
  
  .test-alert-form-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .test-alert-form-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
}

/* Dispositivos muito pequenos */
@media (max-width: 576px) {
  .telegram-section {
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  
  .telegram-test-section {
    padding: 16px;
  }
  
  .test-alert-form {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  .test-alert-button {
    width: 100%;
    margin-top: 8px;
    height: 44px; /* Altura padrão para botões em iOS */
  }
  
  .test-alert-input {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* Input com estilo iOS */
  .test-alert-input input {
    border-radius: 10px;
    padding: 12px;
    font-size: 16px; /* Tamanho de fonte recomendado para mobile */
  }
  
  /* Reduz ainda mais a altura do scroll para telas muito pequenas */
  .responsaveis-scroll-container {
    max-height: 160px;
    border-radius: 10px;
  }
  
  .responsavel-item {
    padding: 14px;
    margin-bottom: 8px;
    border-radius: 12px;
  }
  
  /* Ajustes para a seção de teste de alerta */
  .telegram-test-alert-section {
    position: relative;
    min-height: 180px;
    margin-top: 12px;
    padding: 16px;
  }
  
  .test-alert-header h6 {
    font-size: 17px; /* Tamanho de fonte iOS */
    margin-bottom: 8px;
  }
  
  .test-alert-header p {
    font-size: 14px;
    margin-bottom: 16px;
  }
  
  .test-alert-form-container {
    padding-bottom: 4px;
  }
  
  /* Força a visibilidade da seção de teste */
  .telegram-test-alert {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  /* Melhorias para botões de ação */
  .responsavel-actions {
    gap: 10px;
  }
  
  .responsavel-actions button {
    min-width: 40px;
    height: 40px;
  }
}

/* Específico para iPhone 12 Pro */
@media (max-width: 390px) {
  .telegram-section {
    padding: 12px;
    margin-bottom: 12px;
  }
  
  .responsaveis-scroll-container {
    max-height: 120px;
  }
  
  .test-alert-form-container {
    margin-top: 10px;
    padding: 0 8px 15px 8px;
    overflow: hidden;
    width: calc(100% - 16px);
  }
  
  .telegram-test-alert-section {
    min-height: 200px;
    margin-top: 12px;
    padding: 12px;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
  }
  
  .test-alert-header {
    margin-bottom: 12px;
  }
  
  .test-alert-header h6 {
    font-size: 16px;
    margin-bottom: 6px;
    white-space: normal;
    word-wrap: break-word;
  }
  
  .test-alert-header p {
    font-size: 13px;
    margin-bottom: 12px;
    white-space: normal;
    word-wrap: break-word;
  }
}



@media (max-width: 480px) {
  .responsavel-info {
    gap: 5px;
  }
  
  .responsavel-info .nome {
    font-size: 14px;
  }
  
  .responsavel-info .telefone {
    font-size: 13px;
  }
}

/* Estilos específicos para Alertas Telegram - ajuste final */
.telegram-settings-container {
  width: 100%;
  display: block;
  min-height: 200px;
}

.telegram-test-alert-section {
  margin-top: 25px;
  margin-bottom: 24px;
}
</style>